import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, throwError } from 'rxjs'
import { catchError, map, shareReplay } from 'rxjs/operators'
import {
  PositionFieldCatalog,
  RpsSource,
  SourceFieldCatalog,
} from '../models/field-catalog.model'
import { FieldCatalogService } from './field-catalog.service'

/**
 * With `<base href="/helios/">` this resolves to
 * `/helios/helios-flex-positions/flex-positions/fields`, which the dedicated
 * `helios-flex-positions` rule in proxy.conf.beta.json rewrites onto
 * `https://helios-flex-positions.uswe2.beta.aws.cwan.io/api/flex-positions/fields`.
 *
 * The two `flex-positions` segments are both load-bearing and not a typo: the first is the routing
 * prefix the proxy matches on and strips, the second is the service's own API path under `/api`.
 *
 * The service lives on its own host rather than behind the helios-ui origin, so it needs that
 * explicit proxy entry ahead of the generic `/helios/helios-*` catch-all — the catch-all would
 * otherwise send it to helios-ui and 404. The CIAM bearer token is attached globally by
 * AuthTokenInterceptor.
 */
export const FIELD_CATALOG_URL = `helios-flex-positions/flex-positions/fields`

/** An empty catalog, returned when the backend has no metadata for a source (legitimate, not an
 * error — e.g. CW is not catalogued yet). */
const emptyCatalog = (source: RpsSource): SourceFieldCatalog => ({
  source,
  groups: [],
  columns: [],
})

@Injectable({
  providedIn: 'root',
})
export class HttpFieldCatalogService extends FieldCatalogService {
  /**
   * Cache keyed on `accountId:source`. Every source is scoped to the account, including
   * PROMETHEUS — it was previously cached under a single account-agnostic key on the (wrong)
   * assumption that its catalog is identical for every account, but that meant whichever fetch
   * happened to be first in the browser tab's lifetime (e.g. one that raced a backend still
   * warming up) was replayed forever afterward, for every account, until a full page reload.
   */
  private readonly cache = new Map<string, Observable<SourceFieldCatalog>>()

  constructor(private readonly http: HttpClient) {
    super()
  }

  getCatalog(
    accountId: number,
    source: RpsSource,
  ): Observable<SourceFieldCatalog> {
    const key = this.cacheKey(accountId, source)
    const cached = this.cache.get(key)
    if (cached) return cached

    const request = this.http
      .get<PositionFieldCatalog>(FIELD_CATALOG_URL, {
        params: new HttpParams()
          .set('accountId', String(accountId))
          .set('sources', source),
      })
      .pipe(
        map(
          response =>
            response.sources.find(s => s.source === source) ??
            emptyCatalog(source),
        ),
        // On a terminal error, evict this key so the cache never retains a permanently-errored
        // observable. Without this, shareReplay would replay the same failure to every future
        // caller and the Retry affordance (which re-calls getCatalog) would get the cached failing
        // observable back — never issuing a fresh HTTP request. Evicting here lets Retry re-drive a
        // new GET that can succeed on the second attempt.
        catchError((err: unknown) => {
          if (this.cache.get(key) === request) this.cache.delete(key)
          return throwError(() => err)
        }),
        // Replay the resolved catalog to every grid rebuild without a refetch, and de-dupe
        // concurrent in-flight requests for the same key.
        shareReplay({ bufferSize: 1, refCount: false }),
      )
    this.cache.set(key, request)
    return request
  }

  invalidate(accountId: number): void {
    for (const key of [...this.cache.keys()]) {
      if (key.startsWith(`${accountId}:`)) this.cache.delete(key)
    }
  }

  private cacheKey(accountId: number, source: RpsSource): string {
    return `${accountId}:${source}`
  }
}
